import { useState, useMemo, useCallback } from 'react';

export interface MiscellaneousRegistryFilterState {
  materialType: string[];
  manufacturer: string[];
  location: string[];
  production: string[];
}

interface UseMiscellaneousRegistryFiltersProps {
  rows: any[];
}

const rowMaterialType = (r: any) => {
  const v =
    r?.misc_material_type_name ??
    r?.miscMaterialTypeName ??
    '';
  return v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : '';
};

const rowManufacturer = (r: any) => {
  const v = r?.manufacturer ?? r?.misc_extra_details?.manufacturer;
  return v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : '';
};

const rowLocation = (r: any) => {
  const v = r?.misc_location ?? r?.location ?? '';
  return v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : '';
};

const rowProduction = (r: any) => {
  const v =
    r?.misc_production_type ??
    r?.production_type ??
    r?.productionType ??
    r?.misc_project_stage ??
    '';
  return v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : '';
};

export const useMiscellaneousRegistryFilters = ({
  rows,
}: UseMiscellaneousRegistryFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<MiscellaneousRegistryFilterState>({
    materialType: [],
    manufacturer: [],
    location: [],
    production: [],
  });

  const uniqueMaterialTypes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const t = rowMaterialType(r);
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [rows]);

  const uniqueManufacturers = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const m = rowManufacturer(r);
      if (m) set.add(m);
    });
    return Array.from(set).sort();
  }, [rows]);

  const uniqueLocations = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const l = rowLocation(r);
      if (l) set.add(l);
    });
    return Array.from(set).sort();
  }, [rows]);

  const uniqueProductionTypes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const p = rowProduction(r);
      if (p) set.add(p);
    });
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return rows.filter((r) => {
      const typeStr = rowMaterialType(r).toLowerCase();
      const part = String(r?.misc_part_number ?? r?.part_number ?? r?.partNumber ?? '').toLowerCase();
      const heat = String(r?.misc_heat_number ?? r?.heat_number ?? r?.heatNumber ?? '').toLowerCase();
      const lot = String(r?.misc_lot_number ?? r?.lot_number ?? r?.lotNumber ?? '').toLowerCase();
      const job = String(r?.misc_job_number ?? r?.job_number ?? r?.jobNumber ?? '').toLowerCase();
      const po = String(r?.misc_po_number ?? r?.po_number ?? r?.poNumber ?? '').toLowerCase();
      const qty = String(r?.misc_quantity ?? r?.quantity ?? '').toLowerCase();
      const size = String(r?.misc_size_or_weight ?? r?.size_or_weight ?? r?.sizeOrWeight ?? '').toLowerCase();
      const mfr = rowManufacturer(r).toLowerCase();
      const loc = rowLocation(r).toLowerCase();
      const prod = rowProduction(r).toLowerCase();
      const notes = String(r?.misc_notes ?? r?.notes ?? '').toLowerCase();

      const matchesSearch =
        !q ||
        typeStr.includes(q) ||
        part.includes(q) ||
        heat.includes(q) ||
        lot.includes(q) ||
        job.includes(q) ||
        po.includes(q) ||
        qty.includes(q) ||
        size.includes(q) ||
        mfr.includes(q) ||
        loc.includes(q) ||
        prod.includes(q) ||
        notes.includes(q) ||
        String(r?.id ?? '').includes(q);

      const matchesMaterialType =
        filters.materialType.length === 0 ||
        filters.materialType.includes(rowMaterialType(r));

      const matchesManufacturer =
        filters.manufacturer.length === 0 ||
        filters.manufacturer.includes(rowManufacturer(r));

      const matchesLocation =
        filters.location.length === 0 || filters.location.includes(rowLocation(r));

      const matchesProduction =
        filters.production.length === 0 ||
        filters.production.includes(rowProduction(r));

      return (
        matchesSearch &&
        matchesMaterialType &&
        matchesManufacturer &&
        matchesLocation &&
        matchesProduction
      );
    });
  }, [rows, searchQuery, filters]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery.trim() !== '' ||
      filters.materialType.length > 0 ||
      filters.manufacturer.length > 0 ||
      filters.location.length > 0 ||
      filters.production.length > 0
    );
  }, [searchQuery, filters]);

  const onFilterChange = useCallback(
    (key: keyof MiscellaneousRegistryFilterState) => (value: string[]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      materialType: [],
      manufacturer: [],
      location: [],
      production: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueMaterialTypes,
    uniqueManufacturers,
    uniqueLocations,
    uniqueProductionTypes,
    filteredRows,
    hasActiveFilters,
    filteredCount: filteredRows.length,
    onResetFilters,
  };
};
